/**
 * The etching vocabulary: how a region becomes strokes.
 *
 * Etch is the product's name and its claim ("Einprägen"), so the field behind
 * the auth card is drawn the way a plate is worked, not the way a poster is
 * printed. Tone is never a fill: it is built from burin strokes — parallel
 * lines at one angle, crossed by a second set where the plate should go
 * darker. Every stroke swells in the middle and tapers to a point, and starts
 * or stops a little short of the edge (or runs a little past it), so the
 * geometry stays Bauhaus while the surface reads as hand-cut.
 *
 * Pure geometry in CSS pixels. Nothing here knows about tenants, tokens or
 * React; it is seeded, so the same plate is cut the same way every time.
 */
import { rng } from './tenantIdentity.js';

const RAD = Math.PI / 180;
const round = (v) => Math.round(v * 10) / 10;

/**
 * Clip the line p(t) = b + d·t (|d| = 1) against one region and return the
 * parameter interval [lo, hi] inside it, or null when the line misses.
 *
 * Regions: `disc` {cx, cy, r}; `arc` {cx, cy, r, u} — the half-disc on the
 * side of the centre the unit vector `u` points to; `rect` {x0, y0, x1, y1},
 * axis-aligned (the caller rotates the result for a leaning slab).
 */
function clip(region, b, d, span) {
  let lo = -span;
  let hi = span;

  if (region.kind === 'rect') {
    // Liang–Barsky, one axis at a time.
    const axes = [
      [d[0], region.x0 - b[0], region.x1 - b[0]],
      [d[1], region.y0 - b[1], region.y1 - b[1]],
    ];
    for (const [dir, min, max] of axes) {
      if (Math.abs(dir) < 1e-9) {
        if (min > 0 || max < 0) return null;
      } else {
        lo = Math.max(lo, Math.min(min / dir, max / dir));
        hi = Math.min(hi, Math.max(min / dir, max / dir));
      }
    }
    return hi - lo > 2 ? [lo, hi] : null;
  }

  // |b' + d·t|² ≤ r² with b' relative to the centre and |d| = 1:
  // t² + 2(b'·d)t + (|b'|² − r²) ≤ 0.
  const bx = b[0] - region.cx;
  const by = b[1] - region.cy;
  const bd = bx * d[0] + by * d[1];
  const disc = bd * bd - (bx * bx + by * by - region.r * region.r);
  if (disc <= 0) return null;
  const root = Math.sqrt(disc);
  lo = Math.max(lo, -bd - root);
  hi = Math.min(hi, -bd + root);

  if (region.kind === 'arc') {
    // Keep the half the bulge points into: (b' + d·t)·u ≥ 0, linear in t.
    const num = bx * region.u[0] + by * region.u[1];
    const den = d[0] * region.u[0] + d[1] * region.u[1];
    if (Math.abs(den) < 1e-9) {
      if (num < 0) return null;
    } else if (den > 0) {
      lo = Math.max(lo, -num / den);
    } else {
      hi = Math.min(hi, -num / den);
    }
  }
  return hi - lo > 2 ? [lo, hi] : null;
}

/**
 * Hatch a region with parallel burin strokes.
 *
 * @param {object} region  see `clip`
 * @param {{angle: number, spacing: number, weight: number, reach: number}} hatch
 *   `angle` in degrees, `spacing` in px between strokes, `weight` the stroke's
 *   full thickness in px, `reach` how loosely the hand treats the edge — as a
 *   multiple of the spacing that a stroke may fall short by.
 * @param {number} seed
 * @returns {Array<{x0: number, y0: number, x1: number, y1: number, t: number}>}
 */
export function hatchRegion(region, hatch, seed) {
  const next = rng(seed);
  const d = [Math.cos(hatch.angle * RAD), Math.sin(hatch.angle * RAD)];
  const n = [-d[1], d[0]];
  const isRect = region.kind === 'rect';
  const cx = isRect ? (region.x0 + region.x1) / 2 : region.cx;
  const cy = isRect ? (region.y0 + region.y1) / 2 : region.cy;
  const span = (isRect ? Math.hypot(region.x1 - region.x0, region.y1 - region.y0) / 2 : region.r) + hatch.spacing;

  const strokes = [];
  // A seeded phase, so two plates with the same spacing never share a grid.
  for (let k = -span + next() * hatch.spacing; k <= span; k += hatch.spacing) {
    const b = [cx + n[0] * k, cy + n[1] * k];
    const hit = clip(region, b, d, span);
    if (!hit) continue;
    // Each end falls short by up to `reach` spacings, or overruns by up to
    // a third of one — the difference between cut and printed.
    const lo = hit[0] + hatch.spacing * (next() * (hatch.reach + 0.3) - 0.3);
    const hi = hit[1] - hatch.spacing * (next() * (hatch.reach + 0.3) - 0.3);
    if (hi - lo < 2) continue;
    strokes.push({
      x0: round(b[0] + d[0] * lo),
      y0: round(b[1] + d[1] * lo),
      x1: round(b[0] + d[0] * hi),
      y1: round(b[1] + d[1] * hi),
      t: round(hatch.weight * (0.75 + next() * 0.4)),
    });
  }
  return strokes;
}

/**
 * The path of one burin stroke: a lens that is `t` wide at its middle and
 * comes to a point at both ends. Filled, not stroked, so the taper is real.
 */
export function burinPath({ x0, y0, x1, y1, t }) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  // A quadratic curve bulges half its control offset at the midpoint, so the
  // control sits `t` off the line for a lens `t` wide.
  const nx = (-dy / len) * t;
  const ny = (dx / len) * t;
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  return `M${x0} ${y0}Q${round(mx + nx)} ${round(my + ny)} ${x1} ${y1}Q${round(mx - nx)} ${round(my - ny)} ${x0} ${y0}Z`;
}
